import { useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { audienceSentence } from "../../../shared/goals/audience";
import type { Plan } from "../../../shared/goals/plan";
import { READINESS } from "../../../shared/goals/readiness";
import { usePhone } from "../../../shared/phone/media";
import { PhoneNav, type PhoneBack } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { Split } from "../../../shared/phone/Split";
import { Icon } from "../../components/ui/Icon";
import type { PlanComment } from "../../lib/goalsApi";
import { PLAN_PARTS, planBodies } from "./FullPlan";
import { Spin } from "./Native";
import { metaTests } from "./Review";
import { fmt, readGoal } from "./text";

/**
 * A plan waiting for the owner, the native way: its parts as a list that opens one part at a time (beside
 * the list on an iPad), what the plan expects, and "Not right" to say what should change.
 */

const plural = (n: number, one: string, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
const firstSentence = (s: string) => s.match(/^.+?[.!?](?=\s|$)/)?.[0] ?? s;

/** One line under each part's name: enough to know whether to open it. */
function teaser(P: Plan, id: string): string {
  switch (id) {
    case "short":
      return firstSentence(P.summary);
    case "goal":
      return P.goal.pace_needed;
    case "found":
      return `${plural(P.findings.length, "finding")}, with where each came from`;
    case "lessons":
      return plural(P.lessons_used.length, "lesson");
    case "who":
      return !P.audience ? "" : "places" in P.audience ? audienceSentence(P.audience).map((x) => x.text).join("") : P.audience.who;
    case "angles": {
      const first = P.angles.toSorted((a, b) => a.rank - b.rank)[0];
      return `${plural(P.angles.length, "idea")}${first ? `, “${first.name}” first` : ""}`;
    }
    case "tests":
      return `${plural(P.tests.length, "test")}${P.tests[0] ? `, decided on ${P.tests[0].metric}` : ""}`;
    case "money":
      return P.budget.split.map((b) => `${b.item} $${fmt(b.usd)}`).join(", ");
    case "time":
      return `${plural(P.timeline.length, "step")}${P.timeline.length ? ` to ${P.timeline[P.timeline.length - 1].when}` : ""}`;
    case "run": {
      const ask = P.autonomy.filter((a) => a.mode === "ask").length;
      return `${plural(P.autonomy.length - ask, "thing")} on its own, ${fmt(ask)} ask you first`;
    }
    case "risks":
      return plural(P.risks.length, "risk");
    case "asks":
      return plural(P.needs_from_you.length, "thing");
    default:
      return `Follows ${plural(P.playbook_followed.length, "rule")}${P.conflicts.length ? `, pushes back on ${P.conflicts.length}` : ""}`;
  }
}

/** The parts this plan has: a plan with no Meta audience has no Who. */
export function planParts(plan: Plan) {
  const bodies = planBodies(plan);
  return PLAN_PARTS.filter(([id]) => bodies[id] !== null).map(([id, title]) => ({ id, title, body: bodies[id], teaser: teaser(plan, id) }));
}

/** The goal's sentence with its number and its date in the accent colour, as the goal page shows it. */
export function Highlighted({ text }: { text: string }) {
  const { marks } = readGoal(text);
  const out: ReactNode[] = [];
  let at = 0;
  for (const [a, b] of marks) {
    out.push(text.slice(at, a), <em key={a}>{text.slice(a, b)}</em>);
    at = b;
  }
  out.push(text.slice(at));
  return <>{out}</>;
}

interface PlanScreenProps {
  plan: Plan;
  version: number;
  approved: boolean;
  sentence: string;
  /** The product's square, beside the goal. */
  mark: ReactNode;
  /** Where the plan lives: `${base}/plan`, and one part at `${base}/plan/:part`. */
  base: string;
  partId: string | null;
  back: PhoneBack;
  /** Not right and Approve, under the parts. */
  foot: ReactNode;
}

/** The plan to review: its parts as rows, each pushing its part; on an iPad the open part sits beside them. */
export function PlanScreen({ plan, version, approved, sentence, mark, base, partId, back, foot }: PlanScreenProps) {
  const phone = usePhone();
  const parts = planParts(plan);
  const at = Math.max(0, parts.findIndex((p) => p.id === partId));
  const part = parts[at];
  const next = parts[at + 1];
  const home = `${base}/plan`;

  const list = (
    <div className="pn-screen">
      <PhoneNav title="The plan" large back={back} sub={`Version ${version}, ${approved ? "approved" : "waiting for you"}`} />
      <div className="pn-stack">
        <div className="su-gref">
          {mark}
          <span>{sentence}</span>
        </div>
        <section>
          <div className="pn-cap">{plural(parts.length, "part")}</div>
          <div className="pn-grp">
            {parts.map((p, k) => (
              <Link key={p.id} className={`pn-row su-part${!phone && k === at ? " on" : ""}`} to={`${home}/${p.id}`} replace={!phone} aria-current={!phone && k === at ? "page" : undefined}>
                <span className="su-n">{k + 1}</span>
                <span className="m">
                  <span className="t">{p.title}</span>
                  {p.teaser ? <span className="s">{p.teaser}</span> : null}
                </span>
                <Chevron />
              </Link>
            ))}
          </div>
        </section>
      </div>
      {phone ? foot : null}
    </div>
  );

  const detail = part ? (
    <div className="pn-screen su-partx" key={part.id}>
      <PhoneNav title={part.title} large back={phone ? { to: home, label: "The plan" } : undefined} sub={`Part ${at + 1} of ${parts.length}`} />
      <div className="pn-stack tight">
        <div className="ga gw su-pbody">{part.body}</div>
      </div>
      {next ? (
        <div className="pn-foot">
          <Link className="pn-btn pri wide" to={`${home}/${next.id}`} replace>
            Next: {next.title}
            <Icon name="right" />
          </Link>
        </div>
      ) : phone ? (
        foot
      ) : null}
    </div>
  ) : null;

  const split = <Split list={list} detail={detail} showDetail={!!partId && !!part} />;
  if (phone) return split;
  return (
    <div className="pn-screen su-planx">
      {split}
      {foot}
    </div>
  );
}

/** What has to be ready before anything runs, from the plan: the first steps after approval. */
export function AfterApprove({ plan }: { plan: Plan }) {
  const ideas = metaTests(plan)[0]?.variants.length ?? 0;
  const needs = [...new Set(plan.starts.flatMap((s) => s.needs))].filter((n) => !(ideas > 0 && n === "ad_creative_approval"));
  if (!needs.length && !ideas) return null;
  return (
    <section>
      <div className="pn-cap">After you approve</div>
      <div className="pn-grp">
        {ideas > 0 && <PnRow icon={<Icon name="film" />} iconTone="plain" title={`Add the ${ideas} ad videos`} />}
        {needs.map((n) => (
          <PnRow key={n} icon={<Icon name="check" />} iconTone="plain" title={READINESS[n]?.label ?? n} />
        ))}
      </div>
    </section>
  );
}

/** What the plan expects, against the goal's number. */
export function Expects({ plan, target }: { plan: Plan; target: number }) {
  return (
    <section>
      <div className="pn-cap">What the plan expects</div>
      <div className="pn-grp">
        <PnRow title="Results" value={`${plan.headline.expected} of ${fmt(target)}`} />
        <PnRow title="Each" value={plan.headline.cost} />
        <PnRow title="Pace" value={plan.headline.pace} />
      </div>
    </section>
  );
}

/**
 * "Not right": the owner says what should change, and the agent writes the plan again. Comments left on the
 * full plan go with it.
 */
export function NotRightSheet({ open, onClose, unsent, busy, onSend }: { open: boolean; onClose: () => void; unsent: PlanComment[]; busy: boolean; onSend: (ask: string | null) => void }) {
  const [ask, setAsk] = useState("");
  const [empty, setEmpty] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const send = () => {
    if (!ask.trim() && !unsent.length) {
      setEmpty(true);
      box.current?.focus();
      return;
    }
    onSend(ask.trim() || null);
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Not right"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" disabled={busy} onClick={send}>
          {busy ? <Spin /> : null}
          Send to the agent
        </button>
      }
    >
      <p className="su-sheetsub">Say what should change. The agent answers with a new version of the plan, and you decide.</p>
      {unsent.length > 0 && (
        <section className="su-sheetsec">
          <div className="pn-cap">{unsent.length === 1 ? "Your comment, sent with it" : `Your ${unsent.length} comments, sent with it`}</div>
          <div className="pn-grp">
            {unsent.map((c) => (
              <div className="pn-row static su-full" key={c.id}>
                <span className="m">
                  <span className="t">{c.body}</span>
                  <span className="s">{c.section}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="pn-form">
        <textarea
          ref={box}
          className="pn-inp su-17"
          rows={5}
          placeholder="For example: start with one city, not three"
          aria-label="What should change"
          aria-invalid={empty}
          value={ask}
          onChange={(e) => {
            setAsk(e.target.value);
            setEmpty(false);
          }}
        />
        {empty && <p className="su-err">Say what should change first.</p>}
      </div>
    </Sheet>
  );
}

/** The goal's head on its review: the product, the goal in its own words, where it stands and its money. */
export function GoalHead({ mark, product, sentence, state, budget }: { mark: ReactNode; product: string; sentence: string; state: string; budget: string }) {
  return (
    <div className="su-ghead">
      <div className="su-ghp">
        {mark}
        <span>{product}</span>
      </div>
      <h1>
        <Highlighted text={sentence} />
      </h1>
      <div className="su-ghm">
        <span className="pn-pill violet">
          <i />
          {state}
        </span>
        <span>{budget}</span>
      </div>
    </div>
  );
}
